import axios, { AxiosRequestConfig } from 'axios';
import queryString, { StringifiableRecord } from 'query-string';
import io from 'socket.io-client';
import { Gradient } from '../series/index/series-index.store';
import { API_BASE_URL } from './app.config';
import appStore from './app.store';

/**
 * HTTP 客户端
 */

export const apiHttpClient = axios.create({
  baseURL: API_BASE_URL,
});

type RefreshQueueCallback = (toke: string) => void;

let isRefreshing = false;
let refreshQueue: RefreshQueueCallback[] = [];

// 不用刷新令牌的接口
const isWhiteListedApi = (request: AxiosRequestConfig) => {
  return ['/login', '/refresh-token'].some(api => api == request.url);
};

// 是否需要刷新令牌
const shoudRefreshToken = (request: AxiosRequestConfig) => {
  const refreshToken = appStore.getters['auth/refreshToken'];
  const isAccessTokenExpired = appStore.getters['auth/isAccessTokenExpired'];
  const isRefreshTokenExpired = appStore.getters['auth/isRefreshTokenExpired'];
  const isLoading = appStore.getters['auth/login/loading'];

  return (
    !isRefreshTokenExpired &&
    isAccessTokenExpired &&
    !isWhiteListedApi(request) &&
    refreshToken &&
    !isLoading
  );
};

// 刷新令牌
export const refreshToken = async (
  request: AxiosRequestConfig,
): Promise<AxiosRequestConfig> => {
  return appStore
    .dispatch('auth/login/refreshToken', {
      refreshToken: appStore.getters['auth/refreshToken'],
    })
    .then(response => {
      // 设置头部
      request.headers['Authorization'] = 'Bearer ' + response.data.accessToken;

      refreshQueue.map(refreshQueueCallback =>
        refreshQueueCallback(response.data.accessToken),
      );

      // 重置
      isRefreshing = false;
      refreshQueue = [];

      return Promise.resolve(request);
    });
};

/**
 * Axios 请求拦截器
 */
apiHttpClient.interceptors.request.use(async request => {
  if (appStore.getters['auth/isRefreshTokenExpired']) {
    await appStore.dispatch('auth/logout');
    await appStore.dispatch('notification/pushMessage', {
      content: '请重新登录~',
    });

    throw new axios.Cancel('取消请求 ~');
  }

  // 放行请求
  if (
    isWhiteListedApi(request) ||
    (!shoudRefreshToken(request) && !isRefreshing) ||
    !appStore.getters['auth/refreshToken']
  ) {
    return request;
  }

  // 刷新令牌
  if (shoudRefreshToken(request)) {
    isRefreshing = true;
    await refreshToken(request);

    return request;
  }

  // 在刷新令牌时将后续请求放入队列
  return new Promise(resolve => {
    refreshQueue.push((token: string) => {
      request.headers['Authorization'] = `Bearer ${token}`;
      resolve(request);
    });
  });
});

/**
 * Axios 响应拦截器
 */
apiHttpClient.interceptors.response.use(
  response => response,
  async error => {
    if (error.status === 403 && error.config.url === '/refresh-token') {
      await appStore.dispatch('auth/logout');
      await appStore.dispatch('notification/pushMessage', {
        content: '请重新登录~',
      });

      // 重置
      isRefreshing = false;
      refreshQueue = [];
    }

    return Promise.reject(error);
  },
);

/**
 * 设置本地存储
 */
// eslint-disable-next-line
export const setStorage = (key: string, value: any): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * 获取本地存储
 */
// eslint-disable-next-line
export const getStorage = (key: string): any => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

/**
 * 处理地址查询符
 */
export const queryStringProcess = (
  queryStringObject: StringifiableRecord,
): string => {
  Object.keys(queryStringObject).forEach(key => {
    if (queryStringObject[key] === undefined || queryStringObject[key] === '') {
      delete queryStringObject[key];
    }
  });

  return queryString.stringify(queryStringObject);
};

/**
 * Socket
 */
export const socket = io(API_BASE_URL, { transports: ['websocket'] });

/**
 * 日期范围
 */
export interface DateTimeRangeItem {
  text: string;
  value: string[];
}

type DateTimeRange = DateTimeRangeItem[];

export const getDateTimeRange = (): DateTimeRange => {
  // 选项
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: 'numeric',
  };

  // 语言
  const { language } = navigator;

  // 现在
  const now = new Date();

  // 当前日期
  const currentDate = new Date()
    .toLocaleDateString(language, options)
    .split('/')
    .join('-');

  // 一个月前
  const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1))
    .toLocaleDateString(language, options)
    .split('/')
    .join('-');

  // 三个月前
  const threeMonthAgo = new Date(now.setMonth(now.getMonth() - 3))
    .toLocaleDateString(language, options)
    .split('/')
    .join('-');

  return [
    {
      text: '全部',
      value: [''],
    },
    {
      text: '一天',
      value: [currentDate, currentDate],
    },
    {
      text: '一个月',
      value: [oneMonthAgo, currentDate],
    },
    {
      text: '三个月',
      value: [threeMonthAgo, currentDate],
    },
  ];
};

/**
 * 解析 JWT
 */
export interface JwtPayload {
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  name: string;
  sid: string;
  sub: string;
}

export const parseJwt = (token: string): JwtPayload | null => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

/**
 * 生成渐变样式
 */
export const generateGradientStyles = (gradients: Gradient[]): string => {
  const gradientStyles = gradients
    .map(gradient => {
      const { type: gradientType, direction, stops } = gradient;

      const gradientStops = stops
        .map(stop => {
          return `${stop.color} ${stop.colorStop}`;
        })
        .join(',');

      const gradientStyle = `
        ${gradientType}(
          ${direction}, 
          ${gradientStops}
        )
      `;

      return gradientStyle;
    })
    .join(',');

  return gradientStyles;
};
